import React, { useState, useRef, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { useLocation, useRouteMatch, useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessageWrappedInSpan } from '../../misc';
import { triggerStateChangeForGivenTime } from '../../../utils/common';
import ExtendedSearchCategorySearchTab from './ExtendedSearchCategorySearchTab';
import ExtendedSearchGpsSearchTab from './ExtendedSearchGpsSearchTab';
import ExtendedSearchDualSearchTab from './ExtendedSearchDualSearchTab';
import ExtendedSearchSmartSearchTab from './ExtendedSearchSmartSearchTab';
import StartNumberSearch from '../event/StartNumberSearch';
import ContentControlledCarousel from '../../misc/ContentControlledCarousel';
import SubsectionInFrame from '../../misc/SubsectionInFrame';
import EventSearchAltSearchResultsHint from './EventSearchAltSearchResultsHint';
import {
  MAIN_SEARCH,
  CATEGORY,
  GPS,
  DUAL,
  SMART_SEARCH,
  MAIN_SEARCH_FACE,
  PRESALE,
} from '../../../utils/variables';

////////////////////////////////////////////
// TODO MISSING TEST (make sure main search is set as default)
////////////////////////////////////////////

const EventSearchOptions = ({
  eventSearchOptionsInfo,
  hasSmartSearchTab,
  activeSlideName,
  startNumber,
  searchStartnumber,
  codeSecondFactorForFaceEvent,
  setMainSearchFormRef,
  onInputChange,
  onSearchTrigger,
  onExtendedSearchHintClosed,
  secondFactor,
  showSearchInstructionsDialog,
  hasExtendedSearch,
  showHowToBuyHint,
  onShowHowToBuyHint,
  isAccessGrantedByValidCodeFaceEvent,
  hasUserAccessToMainSearch,
  setSearchType,
  goBackToMainSearchResults,
  triggerPointingAnimation,
  hasDualSearchTab,
}) => {
  const [animate, setAnimate] = useState(false);
  const ALT_HINT = 'alternative_option_hint';
  const location = useLocation();
  const match = useRouteMatch();
  const history = useHistory();

  const eventSearchOptionsRef = useRef();

  useEffect(() => {
    if (setMainSearchFormRef) {
      setMainSearchFormRef(eventSearchOptionsRef);
    }
  }, [eventSearchOptionsRef, setMainSearchFormRef]);

  useEffect(() => {
    if (triggerPointingAnimation) {
      triggerStateChangeForGivenTime(setAnimate, true, false, 2000)
    }
  }, [triggerPointingAnimation])

  useEffect(() => {
    const isOnCategoryPage = match.url.includes('category')
    if (notOnMainSearchPage() && !isOnCategoryPage) {
      setSearchType(ALT_HINT);
    } else if (!isOnCategoryPage && hasUserAccessToMainSearch) {
      setSearchType(MAIN_SEARCH);
    }
  }, [notOnMainSearchPage]);

  const notOnMainSearchPage = () => {
    return /dual|category|gps|smartsearch/.test(match.url);
  };

  const isDualActive = activeSlideName === DUAL;

  const handleNavigateTo = (url) => {
    let fullUrl = url;
    if (codeSecondFactorForFaceEvent || location.search) {
      const codeAsSearchParam = codeSecondFactorForFaceEvent
        ? `?code=${codeSecondFactorForFaceEvent}`
        : location.search;
      fullUrl = `${fullUrl}${codeAsSearchParam}`;
    }

    if (match.path === '/:language/event/:id') {
      history.push(fullUrl);
    } else {
      history.replace(fullUrl);
    }
  };

  const renderSearchTitle = () => {
    if (activeSlideName === MAIN_SEARCH) {
      return (
        <FormattedMessageWrappedInSpan
          id="eventSearchOptions.mainSearchTitle"
          defaultMessage="Search and order your individual package"
          className="typography uppercase typography-title_small"
        />
      );
    } else {
      return (
        <FormattedMessageWrappedInSpan
          id="eventSearchOptions.altSearchTitle"
          defaultMessage="Buy individual photos or add more to your package"
          className="typography uppercase typography-title_small"
        />
      );
    }
  };

  const backButton = (
    <button
      className="full-width secondary-new subsection-action-button btn-in-media-list"
      onClick={() => {
        if (notOnMainSearchPage()) {
          goBackToMainSearchResults();
        } else setSearchType(MAIN_SEARCH);
      }}
    >
      <FormattedMessageWrappedInSpan
        id="eventSearchOptions.backButton"
        defaultMessage="Back to main search"
        className="typography bold-text typography-title_xsmall"
      />
    </button>
  );

  return (
    <div
      ref={eventSearchOptionsRef}
      className={classNames('subsection-container', {
        'point-to-frame': animate,
      })}
    >
      {!isDualActive && (
        <SubsectionInFrame title={renderSearchTitle()} noSidePadding color="main">
          <ContentControlledCarousel
            activeSlideName={activeSlideName}
            className="row center-xs"
          >
            {hasUserAccessToMainSearch && (
              <StartNumberSearch
                slideName={MAIN_SEARCH}
                info={eventSearchOptionsInfo}
                onInputChange={onInputChange}
                onSearchTrigger={onSearchTrigger}
                onExtendedSearchHintClosed={onExtendedSearchHintClosed}
                startNumber={startNumber}
                secondFactor={secondFactor}
                showSearchInstructionsDialog={showSearchInstructionsDialog}
                hasExtendedSearch={hasExtendedSearch}
                showHowToBuyHint={showHowToBuyHint}
                onShowHowToBuyHint={onShowHowToBuyHint}
                isAccessGrantedByValidCodeFaceEvent={isAccessGrantedByValidCodeFaceEvent}
                codeSecondFactorForFaceEvent={codeSecondFactorForFaceEvent}
              />
            )}
            {hasSmartSearchTab && (
              <ExtendedSearchSmartSearchTab
                slideName={SMART_SEARCH}
                startNumber={startNumber}
                eventId={eventSearchOptionsInfo.sgId}
              />
            )}
            {eventSearchOptionsInfo.searchCollections && (
              <ExtendedSearchCategorySearchTab
                slideName={CATEGORY}
                categories={eventSearchOptionsInfo}
                onNavigateTo={handleNavigateTo}
                eventId={eventSearchOptionsInfo.sgId}
                startNumber={startNumber}
              />
            )}
            {eventSearchOptionsInfo.gps && (
              <ExtendedSearchGpsSearchTab
                slideName={GPS}
                onNavigateTo={handleNavigateTo}
                eventId={eventSearchOptionsInfo.sgId}
                startNumber={startNumber}
              />
            )}
            <EventSearchAltSearchResultsHint
              slideName={ALT_HINT}
              eventHasSearchByFace={
                eventSearchOptionsInfo.mainSearch === MAIN_SEARCH_FACE
              }
            />
          </ContentControlledCarousel>
          {activeSlideName != MAIN_SEARCH && activeSlideName != PRESALE && backButton}
        </SubsectionInFrame>
      )}

      {/* TODO after refactoring StartNumberSearch move this to main carousel - this is temporary workaround, StartNumberSearch component twice in the carousel is breaking navigation completely, workaraound is saving functionality, but animation is broken */}
      {hasDualSearchTab && isDualActive && (
        <SubsectionInFrame title={renderSearchTitle()} noSidePadding color="main">
          <ExtendedSearchDualSearchTab
            slideName={DUAL}
            event={eventSearchOptionsInfo}
            startNumber={startNumber}
            searchStartnumber={searchStartnumber}
          />
          {backButton}
        </SubsectionInFrame>
      )}
    </div>
  );
};

EventSearchOptions.propTypes = {
  eventSearchOptionsInfo: PropTypes.object,
  hasSmartSearchTab: PropTypes.bool,
  activeSlideName: PropTypes.string,
  startNumber: PropTypes.string,
  searchStartnumber: PropTypes.string,
  codeSecondFactorForFaceEvent: PropTypes.string,
  setMainSearchFormRef: PropTypes.func,
  onInputChange: PropTypes.func,
  onSearchTrigger: PropTypes.func,
  onExtendedSearchHintClosed: PropTypes.func,
  secondFactor: PropTypes.string,
  showSearchInstructionsDialog: PropTypes.func,
  hasExtendedSearch: PropTypes.bool,
  showHowToBuyHint: PropTypes.bool,
  onShowHowToBuyHint: PropTypes.func,
  isAccessGrantedByValidCodeFaceEvent: PropTypes.bool,
  hasUserAccessToMainSearch: PropTypes.bool,
  goBackToMainSearchSlide: PropTypes.func,
  setSearchType: PropTypes.func,
  goBackToMainSearchResults: PropTypes.func,
  triggerPointingAnimation: PropTypes.bool,
  hasDualSearchTab: PropTypes.bool,
};

export { EventSearchOptions };
export default createFragmentContainer(EventSearchOptions, {
  eventSearchOptionsInfo: graphql`
    fragment EventSearchOptions_eventSearchOptionsInfo on Event {
      id
      sgId
      mainSearch
      searchDual
      gps
      searchCollections
      ...ExtendedSearchCategorySearchTab_categories
      ...ExtendedSearchDualSearchTab_event
      ...StartNumberSearch_info
    }
  `,
});
